// Gemeinsame Bausteine der Kassen-Verträge. Geld in Cent (brutto, wie an der Kasse ausgezeichnet), Mengen „im
// Gebinde“ in Tausendstel des genannten Gebindes (1 Kasten = 1000), Rabatte und Steuersätze in Basispunkten.
import { zId } from '@shared/ids'
import { z } from 'zod'
import { CASH_KINDS, EVENT_KINDS, PAY_METHODS } from '../lib/labels'

export { POS_DOCUMENT_TYPES, RECEIPT_TYPE, RETURN_TYPE } from '../lib/labels'

/** Kalendertag 'YYYY-MM-DD' (Europe/Berlin). */
export const zDay = z.iso.date({ error: 'Bitte ein gültiges Datum eingeben.' })

export const zOptionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, { error: `Der Text ist zu lang (höchstens ${max} Zeichen).` })
    .nullish()
    .transform((value) => (value === '' ? null : value))

export const zReason = z
  .string({ error: 'Bitte geben Sie einen Grund an.' })
  .trim()
  .min(3, { error: 'Bitte geben Sie einen Grund an (mindestens 3 Zeichen).' })
  .max(300, { error: 'Der Grund ist zu lang (höchstens 300 Zeichen).' })

/** Betrag in Cent, größer als null. */
export const zAmountCents = z
  .int({ error: 'Bitte einen Betrag eingeben.' })
  .min(1, { error: 'Der Betrag muss größer als 0 sein.' })
  .max(10_000_000, { error: 'Der Betrag ist zu hoch.' })

/** Betrag in Cent, null erlaubt. */
export const zAmountCentsOrZero = z
  .int({ error: 'Bitte einen Betrag eingeben.' })
  .min(0, { error: 'Der Betrag darf nicht negativ sein.' })
  .max(10_000_000, { error: 'Der Betrag ist zu hoch.' })

/** Menge im Gebinde in Tausendstel, größer als null. */
export const zSaleQty = z
  .int({ error: 'Bitte eine Menge eingeben.' })
  .min(1, { error: 'Die Menge muss größer als 0 sein.' })
  .max(10_000_000, { error: 'Die Menge ist zu groß.' })

/** Stück in Tausendstel, ganze Stücke. */
export const zPieceQty = z
  .int({ error: 'Bitte eine Stückzahl eingeben.' })
  .min(1000, { error: 'Bitte mindestens 1 Stück angeben.' })
  .max(10_000_000, { error: 'Die Stückzahl ist zu groß.' })
  .multipleOf(1000, { error: 'Leergut wird in ganzen Stücken gezählt.' })

export const zDiscountBp = z
  .int({ error: 'Bitte einen Rabatt eingeben.' })
  .min(0, { error: 'Der Rabatt darf nicht negativ sein.' })
  .max(10_000, { error: 'Der Rabatt liegt zwischen 0 und 100 %.' })

export const zPayMethod = z.enum(PAY_METHODS, { error: 'Bitte wählen Sie Bar, Karte oder Gutschein.' })
export const zCashKind = z.enum(CASH_KINDS, { error: 'Bitte wählen Sie die Art der Bewegung.' })
export const zEventKind = z.enum(EVENT_KINDS)

export const zLocationRef = z.object({ id: zId, code: z.string(), name: z.string() })
export const zPerson = z.object({ id: zId, name: z.string() })
export const zRegisterRef = z.object({ id: zId, code: z.string(), name: z.string(), location: zLocationRef })

/** Gezähltes Leergut auf dem Bon: Pfandart (einfach oder Gebinde) und Stück in Tausendstel. */
export const zEmptiesEntry = z.strictObject({
  depositTypeId: zId,
  emptiesQty: zPieceQty,
})

export const SHIFT_STATES = ['open', 'closed', 'pending_approval', 'approved'] as const
export const zShiftState = z.enum(SHIFT_STATES)

export const zTaxRow = z.object({ rateBp: z.int(), netCents: z.int(), taxCents: z.int(), grossCents: z.int() })
export const zPaymentRow = z.object({
  method: z.string(),
  label: z.string(),
  amountCents: z.int(),
  /** nur bar: gegeben und Rückgeld */
  givenCents: z.int().nullable(),
  changeCents: z.int().nullable(),
  /** Gutschein: Code; Karte: Belegnummer des Terminals */
  reference: z.string().nullable(),
})
